// Debt Types
export type DebtType = 
  | 'credit_card' 
  | 'personal_loan' 
  | 'student_loan' 
  | 'auto_loan' 
  | 'medical' 
  | 'other'

export type DebtPriority = 'high' | 'medium' | 'low'

export interface Debt {
  id: number
  name: string
  debtType: DebtType
  balance: number
  interestRate: number
  monthlyPayment: number
  minimumPayment: number
  creditLimit: number
  utilizationRate: number
  paymentPercentage: number
  dueDate?: string
  notes?: string
}

export interface DebtProjection {
  debtId: number
  debtName: string
  monthsToPayoff: number | string
  totalInterest: number
  totalCost: number
  monthlyInterest: number
  isAccumulating: boolean
}

export interface InterestAccumulationPoint {
  month: number
  balance: number
  interestPaid: number
  totalInterestPaid: number
}

export interface InterestAccumulation {
  debtId: number
  debtName: string
  accumulation: InterestAccumulationPoint[]
}

export interface DebtScenarioPoint {
  month: number
  balance: number
  interest: number
  monthlyPayment: number
  principalPayment: number
  totalInterest: number
}

export interface DebtAccumulationScenario {
  debtId: number
  debtName: string
  debtType: DebtType
  paymentPercentage?: number
  currentPayment: DebtScenarioPoint[]
  minimumPayment?: DebtScenarioPoint[]
  reducedPayment?: DebtScenarioPoint[]
  noPayment: DebtScenarioPoint[]
}

export interface DebtAnalysis {
  totalInterestPaid: number
  totalInterestToPay: number
  totalBalance: number
  monthsToPayoff: number
  totalCost: number
  monthlyInterestExpense: number
  debtAccumulation: DebtAccumulationScenario[]
  payoffProjections: DebtProjection[]
  interestAccumulation: InterestAccumulation[]
}

export interface DebtState {
  debts: Debt[]
  totalBalance: number
  totalMonthlyPayments: number
  averageInterestRate: number
  totalCreditLimit: number
  totalCreditUtilization: number
  highInterestDebts: number
  creditCardDebts: number
  totalMinimumPayments: number
  debtAnalysis: DebtAnalysis
}

// Utility function to determine priority
export function getDebtPriority(debt: Debt): DebtPriority {
  if (debt.interestRate >= 20 || debt.utilizationRate >= 80) {
    return 'high'
  }
  if (debt.interestRate >= 10 || debt.utilizationRate >= 50) {
    return 'medium'
  }
  return 'low'
}
